import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWikiV2Client } from '../../../hooks/useWikiV2';
import WikiIcon from '../shared/WikiIcon';
import { invalidateWiki, wikiKeys } from '../shared/useWikiQueries';
import { wikiUi } from '../shared/wikiUi';
import { WikiLoading } from '../shared/WikiStates';
import WikiModalShell, { WikiCreateButton, WikiModalError } from '../modals/WikiModalShell';
import { wikiModalErrorText } from '../modals/wikiModalErrors';
import WikiAccessFields from './WikiAccessFields';
import { useAccessAncestor, useWikiAcl, useWikiMembers } from './useWikiAccess';
import {
    WIKI_ACCESS_COPY,
    accessChoicesReady,
    choicesToRules,
    defaultAccessChoices,
    rulesToChoices,
} from './wikiAccessRules';
import '../modals/wikiModals.css';
import './wikiAccess.css';

const enc = encodeURIComponent;
const LEVELS = ['none', 'view', 'edit', 'admin'];

export const WIKI_ACCESS_PANEL_COPY = Object.freeze({
    title: (name) => `Access for ${name || 'this item'}`,
    save: 'Save access',
    reset: (nested) => (nested ? 'Reset to inherit' : 'Reset to defaults'),
    saved: 'Access saved',
    resetDone: (nested) => (nested ? 'Access reset. It inherits again.' : 'Access reset to the role defaults.'),
    sourceOwn: 'It has its own access rules.',
    sourceInherited: (parentName) => `It uses ${parentName || 'its parent'}'s access.`,
    sourceDefault: 'It uses the default access for each role.',
    advancedTitle: 'Advanced rules',
    advancedBody: 'These rules were set outside this panel. Change a level and save, or use the simple choices to replace them.',
    useChoices: 'Use the simple choices instead',
    subject: (row, members) => {
        if (row.subject_type === 'role') return WIKI_ACCESS_COPY.roleLabels[row.subject_id === 'sales_rep' ? 'user' : row.subject_id] || row.subject_id;
        const member = (members || []).find((item) => item.user_id === row.subject_id);
        return member ? (member.name || member.email) : row.subject_id;
    },
});

const COPY = WIKI_ACCESS_PANEL_COPY;

/**
 * The Access panel (#871): a Wiki admin sets the real access rules of a
 * category, subcategory or page. Reads `GET …/acl`, saves `PUT …/acl`
 * (replaces the scope's rules; `[]` = inherit / role defaults).
 *
 * Rules the simple choices can express open as choices; anything else
 * (hand-made through the API) opens in the Advanced list and is saved as it
 * is, so nothing is rewritten behind the admin's back.
 */
export default function WikiAccessPanel({ scope, scopeId, name, parentId = null, parentName = '', onClose }) {
    const nested = scope === 'page' || Boolean(parentId);
    const client = useWikiV2Client();
    const queryClient = useQueryClient();
    const acl = useWikiAcl(scope, scopeId);
    const ancestor = useAccessAncestor(parentId, { enabled: nested });
    const membersQuery = useWikiMembers();
    const [choices, setChoices] = useState(null);
    const [advanced, setAdvanced] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    // Seed the form once the current rules arrive.
    useEffect(() => {
        if (!acl.isSuccess || choices || advanced) return;
        const parsed = rulesToChoices(acl.entries, { nested });
        if (parsed) setChoices(parsed);
        else setAdvanced(acl.entries.map((row) => ({ ...row })));
    }, [acl.isSuccess, acl.entries, nested, choices, advanced]);

    const { rules, notCarried } = useMemo(() => (choices
        ? choicesToRules(choices, {
            nested,
            ancestorSummary: ancestor.summary,
            ancestorRows: ancestor.rows,
            members: membersQuery.members,
            roleFloors: membersQuery.roleFloors,
        })
        : { rules: [], notCarried: [] }), [
        choices, nested, ancestor.summary, ancestor.rows, membersQuery.members, membersQuery.roleFloors,
    ]);

    const loading = acl.isLoading || (!choices && !advanced && !acl.isError);
    const ready = advanced
        ? true
        : Boolean(choices)
            && accessChoicesReady(choices, { nested, ancestorSummary: ancestor.summary, roleFloors: membersQuery.roleFloors })
            && ancestor.ready;

    const put = async (entries, message) => {
        if (busy) return;
        setBusy(true);
        setError('');
        try {
            const base = scope === 'page' ? 'pages' : 'categories';
            await client.put(`/wiki/${base}/${enc(scopeId)}/acl`, {
                entries: entries.map((row) => ({
                    scope,
                    scope_id: scopeId,
                    subject_type: row.subject_type,
                    subject_id: row.subject_id,
                    level: row.level,
                })),
            });
            await invalidateWiki(queryClient, 'categories', 'category', 'pages', 'home', 'counts');
            if (scope === 'page') await invalidateWiki(queryClient, wikiKeys.page(scopeId).slice(1));
            onClose();
            wikiUi.toast(message);
        } catch (err) {
            setBusy(false);
            setError(wikiModalErrorText(err));
        }
    };

    const save = () => (advanced ? put(advanced, COPY.saved) : put(rules, COPY.saved));
    const reset = () => put([], COPY.resetDone(nested));

    // Only once the rules are known: an empty list while loading isn't "default".
    let source = '';
    if (acl.isSuccess) {
        source = acl.entries.length
            ? COPY.sourceOwn
            : (nested ? COPY.sourceInherited(parentName) : COPY.sourceDefault);
    }

    return (
        <WikiModalShell
            title={COPY.title(name)}
            onClose={onClose}
            testId="wiki-access-panel"
            footer={(
                <>
                    {acl.entries.length > 0 && (
                        <WikiCreateButton
                            enabled={!busy}
                            busy={busy}
                            onClick={reset}
                            variant="secondary"
                            action="wiki.access_reset"
                            testId="wiki-access-reset"
                        >
                            {COPY.reset(nested)}
                        </WikiCreateButton>
                    )}
                    <WikiCreateButton
                        enabled={ready && !loading}
                        busy={busy}
                        onClick={save}
                        icon="lock"
                        iconSize={14}
                        action="wiki.access_save"
                        testId="wiki-access-save"
                    >
                        {COPY.save}
                    </WikiCreateButton>
                </>
            )}
        >
            {source && (
                <div className="wk-modal-note" data-testid="wiki-access-source">
                    <WikiIcon name="lock" size={14} />
                    <span>{source}</span>
                </div>
            )}

            {loading && <WikiLoading label="Loading access…" />}

            {choices && (
                <WikiAccessFields
                    choices={choices}
                    onChange={setChoices}
                    nested={nested}
                    parentName={parentName}
                    members={membersQuery.members}
                    membersLoading={membersQuery.isLoading}
                    notCarried={notCarried}
                />
            )}

            {advanced && (
                <div className="wk-acc-advanced" data-testid="wiki-access-advanced">
                    <div className="wk-t-name">{COPY.advancedTitle}</div>
                    <p className="wk-t-meta wk-modal-hint">{COPY.advancedBody}</p>
                    <ul className="wk-acc-rows">
                        {advanced.map((row, index) => (
                            <li key={`${row.subject_type}:${row.subject_id}`} className="wk-acc-row">
                                <span className="wk-acc-row-subject">
                                    <WikiIcon name={row.subject_type === 'role' ? 'users' : 'user'} size={14} />
                                    {COPY.subject(row, membersQuery.members)}
                                </span>
                                <select
                                    className="wk-acc-level"
                                    value={row.level}
                                    aria-label={`Level for ${COPY.subject(row, membersQuery.members)}`}
                                    onChange={(event) => setAdvanced((prev) => prev.map((item, i) => (
                                        i === index ? { ...item, level: event.target.value } : item
                                    )))}
                                >
                                    {LEVELS.map((level) => <option key={level} value={level}>{level}</option>)}
                                </select>
                            </li>
                        ))}
                    </ul>
                    <button
                        type="button"
                        className="wk-btn wk-btn--ghost wk-btn--sm"
                        data-testid="wiki-access-use-choices"
                        onClick={() => { setAdvanced(null); setChoices(defaultAccessChoices(nested)); }}
                    >
                        {COPY.useChoices}
                    </button>
                </div>
            )}

            <WikiModalError
                message={error || [acl.error, membersQuery.error, ancestor.error]
                    .filter(Boolean).map((item) => wikiModalErrorText(item))[0] || ''}
            />
        </WikiModalShell>
    );
}
